import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { ChevronRight } from "react-feather";
import CONSTANTS from "../constants";

import {
  FlexContainer,
  Button,
  palette,
  mediaMin,
  fonts,
} from "../ui-components";

const Card = ({
  className,
  details,
  onSelection,
  onCardMouseOver,
  forwardedRef,
}) => {
  const [openStatus, setOpenStatus] = useState(-1);
  const {
    place_id,
    name,
    formatted_address,
    distance,
    photos,
    price_level,
    rating,
    cuisine,
  } = details;

  useEffect(() => {
    if (!details?.opening_hours || !details?.opening_hours?.periods) {
      setOpenStatus(-1);
    } else {
      const today = new Date();
      const day = today.getDay();
      const hours = today.getHours();
      const minutes = today.getMinutes();
      const time = Number(`${hours}${minutes}`);
      const values = details.opening_hours?.periods?.filter(
        (period) => period.open.day === day
      );
      if (
        values.length > 0 &&
        values[0]?.open?.time &&
        values[0]?.close?.time
      ) {
        if (values.length > 1 && values[1].open.time && values[1].close.time) {
          return (time > values[0].open.time && time < values[0].close.time) ||
            (time > values[1].open.time && time < values[1].close.time)
            ? setOpenStatus(1)
            : setOpenStatus(0);
        } else {
          return time > values[0].open.time && time < values[0].close.time
            ? setOpenStatus(1)
            : setOpenStatus(0);
        }
      } else {
        setOpenStatus(-1);
      }
    }
  }, [details]);

  const getCardThumbnail = () => {
    if (!photos || !photos.length > 0) return "";
    const url = `https://maps.googleapis.com/maps/api/place/photo?photo_reference=${photos[0].photo_reference}&sensor=false&maxheight=150&maxwidth=150&key=${CONSTANTS.APIKEY}`;
    return url;
  };

  const getPriceLevel = () => {
    switch (price_level) {
      case 1:
        return "$";
      case 2:
        return "$$";
      case 3:
        return "$$$";
      case 4:
        return "$$$$";
      case 5:
        return "$$$$$";
      default:
        return "$";
    }
  };

  const renderOpenStatus = () => {
    if (openStatus === -1) {
      return <Item>Unknown</Item>;
    } else if (openStatus === 1) {
      return <Item color={palette.green}>OPEN</Item>;
    } else {
      return <Item color={palette.secondary}>CLOSED</Item>;
    }
  };

  return (
    <div ref={forwardedRef}>
      <CardContainer
        className={className}
        onMouseOver={() => onCardMouseOver(place_id)}
        onMouseLeave={() => onCardMouseOver(null)}
        onClick={() => {
          onSelection(details);
          onCardMouseOver(null);
        }}
      >
        <ImageContainer bgImage={getCardThumbnail()} />
        <ContentWrapper>
          <List>
            <Item>
              <span className="cuisine">{cuisine}</span>
            </Item>
            {renderOpenStatus()}
            {distance && <Item>{distance.toFixed(1)} miles</Item>}
            {rating && (
              <Item>
                {rating} <span className="star">★</span>
              </Item>
            )}
            {price_level && <Item>{getPriceLevel()}</Item>}
          </List>
          <TitleHeading size={6}>{name}</TitleHeading>
          <SubHeading>{formatted_address}</SubHeading>
        </ContentWrapper>
        <ButtonContainer alignItems={"center"}>
          <DetailsButton
            color={palette.secondary}
            hoverColor={palette.primary}
            className="view-details-butn"
          >
            Details
            <ChevronRight />
          </DetailsButton>
        </ButtonContainer>
      </CardContainer>
    </div>
  );
};

export { Card };

const CardContainer = styled(FlexContainer)`
  border-radius: 20px;
  overflow: hidden;
  background-color: ${palette.pureWhite};
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
  min-height: 60px;

  &:hover {
    cursor: pointer;

    .view-details-butn {
      border: none;
      background: ${palette.primary} !important;
      color: ${({ hoverColor }) => hoverColor} !important;
      cursor: pointer;
    }
  }
`;

const ImageContainer = styled.div`
  background-image: url(${({ bgImage }) => bgImage || ""});
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-color: ${palette.gray};
  width: 100%;
  max-width: 60px;

  ${mediaMin.tablet_sm`
    max-width: 80px;
  `};

  ${mediaMin.laptop`
    display: block;
  `}
`;

const List = styled.ul`
  display: flex;
  align-items: center;

  li:first-child {
    padding-left: 0;
  }

  li:last-child {
    padding-right: 0;
    border-right: none;
  }
`;

const Item = styled.li`
  font-size: 10px;
  padding: 0 8px;
  color: ${({ color }) => (color ? color : palette.text)};
  border-right: 1px solid #ccc;

  span.star {
    color: ${palette.yellow};
  }

  span.cuisine {
    font-weight: bold;
    text-transform: uppercase;
  }

  ${mediaMin.tablet_sm`
    font-size: 11px;
  `}
`;

const ContentWrapper = styled.div`
  padding: 15px 5px 10px;
  width: 100%;
  margin-left: 5px;

  ${mediaMin.tablet_sm`
    margin-left: initial;
    padding: 15px 10px 15px;
  `};
`;

const TitleHeading = styled.h3`
  margin: 6px 0;
  font-family: ${fonts.primary};
  color: ${palette.primary};
  font-size: 16px;
`;

const SubHeading = styled.p`
  font-size: 13px;
  line-height: 1;
  margin-bottom: 5px;
  margin-top: 0;
`;

const ButtonContainer = styled(FlexContainer)`
  display: none;

  ${mediaMin.laptop`
    display: flex;
    margin-right: 15px;
  `};
`;

const DetailsButton = styled(Button)`
  ${mediaMin.tablet_sm`
    min-width: initial;
  `};
`;
